// TransitionMenu · Mandat (Lot M2)
// Dropdown des transitions autorisées + accès direct à la clôture.
// Ne montre QUE les transitions dans `allowed_next` (drivé par le backend).

import { useState, useRef, useEffect } from "react";
import { ChevronDown, PenLine, PackageCheck, Megaphone,
         MinusCircle, XCircle, Sparkles } from "lucide-react";

const TRANSITION_LABELS = {
  sign:                             { label: "Signer le mandat",           icon: PenLine,     tone: "primary" },
  mark_ready:                       { label: "Marquer prêt à commercialiser", icon: PackageCheck, tone: "primary" },
  start_commercialization:          { label: "Lancer la commercialisation", icon: Megaphone,   tone: "primary" },
  withdraw_from_commercialization:  { label: "Retirer de la commercialisation", icon: MinusCircle, tone: "neutral" },
};

export default function TransitionMenu({
  allowedNext = [],
  canClose = true,
  isTerminal = false,
  busy = false,
  onTransition,
  onCloseClick,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (isTerminal) {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-4 py-2 text-[12.5px] text-black/50"
        data-testid="transition-menu-terminal"
      >
        <Sparkles size={13} className="opacity-60" />
        Mandat clôturé — pas d&apos;action possible
      </span>
    );
  }

  const hasAny = allowedNext.length > 0 || canClose;
  if (!hasAny) return null;

  return (
    <div ref={ref} className="relative" data-testid="transition-menu">
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
        data-testid="transition-menu-trigger"
      >
        Faire évoluer le mandat
        <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-30 w-[290px] rounded-2xl bg-white p-2"
          style={{ boxShadow: "0 12px 40px -12px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(0,0,0,0.06)" }}
          data-testid="transition-menu-panel"
        >
          {allowedNext.map((key) => {
            const meta = TRANSITION_LABELS[key];
            if (!meta) return null;
            const Icon = meta.icon;
            return (
              <button
                key={key}
                onClick={() => { setOpen(false); onTransition?.(key); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] text-black transition-colors hover:bg-black/[0.04]"
                data-testid={`transition-menu-item-${key}`}
              >
                <Icon size={15} className="text-black/60" strokeWidth={1.7} />
                <span className="flex-1">{meta.label}</span>
              </button>
            );
          })}

          {canClose && (
            <>
              {allowedNext.length > 0 && (
                <div className="my-1 h-px bg-black/[0.06]" />
              )}
              <button
                onClick={() => { setOpen(false); onCloseClick?.(); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] text-[#B91C1C] transition-colors hover:bg-red-50"
                data-testid="transition-menu-close"
              >
                <XCircle size={15} strokeWidth={1.7} />
                <span className="flex-1">Clôturer sans vente…</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
